import {observer} from 'mobx-react-lite'
import React, {useEffect, useLayoutEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {logger, Time, Util} from 'yougile-util'
import {Button} from '@components/common/base/buttons'
import {Input} from '@components/common/base/input'
import {TextArea} from '@components/common/base/textarea'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {PageHeader} from '@components/page/page-header'
import {useSyncEventUpdateEffect} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {ChatUtils} from '@extensions/chat-utils'
import {News} from '@extensions/news'
import {
  FeedbackPageContext,
  FeedbackPageStore,
  useFeedbackPageStore
} from './feedback-page-store'

export const FeedbackPage = () => {
  const [projectStore] = useState(() => new FeedbackPageStore())

  return (
    <ErrorBoundary
      silent
      onError={e => logger.error('feedback page error ' + e.message)}
    >
      <FeedbackPageContext.Provider value={projectStore}>
        <FeedbackPageBase />
      </FeedbackPageContext.Provider>
    </ErrorBoundary>
  )
}

const FeedbackPageBase = observer(() => {
  const [state, store] = useFeedbackPageStore()

  useLayoutEffect(() => {
    store.syncOnNew()
    return () => store.dispose()
  }, [])

  useEffect(() => {
    const hnd = ChatUtils.addChatChangeListener(store.updateExpandedChatHubId)
    return () => ChatUtils.removeChatChangeListener(hnd)
  }, [store])

  return (
    <div className="feedback">
      <PageHeader>{Lang.msg.feedbackTitle}</PageHeader>

      <div className="feedback__cnt">
        <WithPerfectScrollbar>
          <div className="feedback__scroll-cnt">
            <div className="feedback__support">
              <div className="feedback__panel-title">
                {Lang.msg.feedback.support}
              </div>
              <div className="feedback__panel-descr">
                {Lang.msg.feedback.supportDescr}
              </div>

              <div className="feedback__form">
                <Input
                  autoFocus
                  value={state.feedback.topic}
                  onChange={({baseElement}) =>
                    store.updateTopic(baseElement?.value ?? '')
                  }
                  placeholder={Lang.msg.feedback.inputTopic}
                  className="text-15"
                />
                <TextArea
                  value={state.feedback.text}
                  onChange={({baseElement: {value}}) => store.updateText(value)}
                  placeholder={Lang.msg.feedback.inputMessage}
                  className="text-15 h-60 mt-10"
                  textAreaClassName="py-8"
                />
                {state.feedback.attachments.map((file, i) => (
                  <div className="feedback__file">
                    <i className="fa fa-file" />
                    {file.name}
                    <span className="feedback__size">
                      ({Util.humanReadableSize(file.size)})
                    </span>
                    <i
                      className="fa fa-trash"
                      onClick={() => store.deleteAttachment(i)}
                    />
                  </div>
                ))}

                <div className="feedback__actions">
                  <div
                    className="feedback__attach"
                    onClick={store.feedbackAttach}
                  >
                    {Lang.msg.feedback.attach}
                  </div>
                  <Button
                    className="feedback__btn"
                    onClick={store.feedbackSubmitNew}
                    disabled={!state.feedback.topic && !state.feedback.text}
                  >
                    {Lang.msg.common.send}
                  </Button>
                </div>
              </div>

              {!!state.openTasks.length && (
                <div className="feedback__subtitle">
                  {Lang.msg.feedback.openRequests}
                </div>
              )}

              <div className="feedback__open">
                {state.openTasks
                  .sort(
                    (a, b) =>
                      (state.openTasksLastTimestamp.get(b.id) ?? 0) -
                      (state.openTasksLastTimestamp.get(a.id) ?? 0)
                  )
                  .map(task => {
                    const newMessagesCount = store.newMessagesCount(task)
                    return (
                      <div
                        key={task.id}
                        className={cn(
                          'feedback__ticket',
                          state.expandedChatHubId === task.id &&
                            'feedback__ticket--chat-open'
                        )}
                        onClick={event => store.openChat(task, event)}
                      >
                        {newMessagesCount > 0 && (
                          <div className="feedback__new-msg">
                            {newMessagesCount}
                          </div>
                        )}
                        <div className="feedback__ticket-title">
                          {task.title}
                        </div>
                        <div className="feedback__ticket-msg">
                          {store.lastMessageText(task)}
                        </div>
                      </div>
                    )
                  })}
              </div>

              {state.closedTasks.length > 0 && (
                <div className="feedback__subtitle">
                  {Lang.msg.feedback.closedRequests}
                </div>
              )}

              <div className="feedback__closed">
                {state.closedTasks
                  .sort(
                    (a, b) =>
                      (state.closedTasksLastTimestamp.get(b.id) ?? 0) -
                      (state.closedTasksLastTimestamp.get(a.id) ?? 0)
                  )
                  .map(task => {
                    const newMessagesCount = store.newMessagesCount(task)
                    return (
                      <div
                        key={task.id}
                        className={cn(
                          'feedback__ticket',
                          state.expandedChatHubId === task.id &&
                            'feedback__ticket--chat-open'
                        )}
                        onClick={event => store.openChat(task, event)}
                      >
                        {newMessagesCount > 0 && (
                          <div className="feedback__new-msg">
                            {newMessagesCount}
                          </div>
                        )}
                        <div className="feedback__ticket-title">
                          {task.title}
                        </div>
                        <div className="feedback__ticket-msg">
                          {store.lastMessageText(task)}
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>

            <NewsList />
          </div>
        </WithPerfectScrollbar>
      </div>
    </div>
  )
})

const NewsList = observer(() => {
  const [state, store] = useFeedbackPageStore()
  useSyncEventUpdateEffect('news', {filter: '*', debounceTime: 1000})
  useSyncEventUpdateEffect('profile', {filter: '*', debounceTime: 1000})

  const newsList = News.list()

  return (
    <div className="feedback__news">
      <div className="feedback__panel-title">{Lang.msg.feedback.news}</div>
      <div className="feedback__panel-descr">{Lang.msg.feedback.newsDescr}</div>
      {state.isEditor && (
        <div className="feedback__add-news" onClick={store.newsAdd}>
          {Lang.msg.addNewsItem}
        </div>
      )}
      <div className="feedback__v-space"></div>
      {newsList.slice(0, state.showLimit).map(task => (
        <div
          key={task.id}
          className={`feedback__news-item ${
            News.isUnseen(task) ? 'feedback__news-item--new' : ''
          }`}
          onClick={event => store.openChat(task, event)}
        >
          <div className="feedback__news-title">{task.title}</div>
          <img
            className="feedback__news-image"
            src={store.newsGetImageUrl(task)}
            alt=""
          />
          <div className="feedback__news-descr">
            {store.newsGetDescription(task)}
          </div>
          <div className="feedback__news-footer">
            <div className="feedback__news-date">
              {Time.formatDate(task.timestamp)}
            </div>
            <div className="feedback__news-comments">
              {task.chat.getMessagesCount()}
            </div>
          </div>
        </div>
      ))}
      {newsList.length > state.showLimit && (
        <div className="feedback__more">
          <div className="_link" onClick={store.newsShowMore}>
            {Lang.msg.showMore2}
          </div>
        </div>
      )}
    </div>
  )
})
